import React, { useEffect, useMemo, useState } from "react";

import Spinner from "../../components/spinner";
import { convertToCloudFrontUrl } from "../../helper";
import { useLanguage } from "../../context/languageContext";
import { commentsApi } from "./commentsApi";

/**
 * The post the comment is on: its words and its pictures.
 *
 * Fetched when the dialog opens rather than carried in the row — the list
 * would be hauling every post's media for 47 rows to show one.
 */
const PostPreview = ({ postId }) => {
  const { adminInfo } = useLanguage();
  const api = useMemo(() => commentsApi(adminInfo?.token), [adminInfo?.token]);

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .post(postId)
      .then((data) => {
        if (!cancelled) setPost(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, postId]);

  if (loading) return <Spinner />;
  // A post that has since been deleted is not an error worth shouting about:
  // the comment on screen is still the thing being judged.
  if (error || !post) {
    return <div className="text-muted small">Couldn't load the post ({error}).</div>;
  }

  return (
    <div>
      <div className="mb-2">
        {post.title || <span className="text-muted">Untitled</span>}
        <div className="text-muted small">
          {post.mediaType} · {post.commentCount} comments · {post.likes} likes
          {post.visible === false ? (
            <span className="text-warning"> · hidden</span>
          ) : null}
        </div>
      </div>

      {post.description ? (
        <p className="small mb-2" style={{ whiteSpace: "pre-wrap" }}>
          {post.description}
        </p>
      ) : null}

      {post.images?.length > 0 ? (
        <div className="d-flex gap-2 flex-wrap">
          {/* Every slide of a guide, in reading order — a comment often
              answers slide four rather than the cover. */}
          {post.images.map((url, i) => (
            <img
              key={`${i}-${url}`}
              src={convertToCloudFrontUrl(url)}
              alt=""
              style={{
                width: 120,
                height: 150,
                objectFit: "cover",
                borderRadius: 8,
                border: "1px solid var(--ps-border)",
              }}
            />
          ))}
        </div>
      ) : (
        <div className="text-muted small">No media on this post.</div>
      )}

      {post.videoUrl ? (
        <a
          className="btn btn-sm btn-outline-secondary mt-2"
          href={convertToCloudFrontUrl(post.videoUrl)}
          target="_blank"
          rel="noreferrer"
        >
          Play the reel
        </a>
      ) : null}
    </div>
  );
};


export default PostPreview;
